import { TPurchaseBillingPeriod } from '../../../../../shared/common/purchases';
import { getPathLocale, setPathLocale, SITE_LANGUAGES } from '../../../../../shared/common/localeShared';
import { TXsollaSubscriptionOperation } from '../../../../../shared/common/xsolla';
import { TXsollaSubscriptionTier } from '../../../../../shared/common/xsollaConfig';
import { namedRoutes, resolveRoutePath } from '../bundles/routes';

type TXsollaCheckoutSuccessBillingPeriod = Extract<TPurchaseBillingPeriod, 'monthly' | 'annual'>;

export type TXsollaCheckoutSuccessParams = {
  tier: TXsollaSubscriptionTier | null;
  billingPeriod: TXsollaCheckoutSuccessBillingPeriod | null;
  operation?: TXsollaSubscriptionOperation;
  activated: boolean;
};

type TXsollaCheckoutSuccessPathParams = Partial<TXsollaCheckoutSuccessParams> & {
  locale?: string | null;
};

function resolvePremiumCheckoutSuccessLocale(locale?: string | null): string | null {
  const normalizedLocale = String(locale || '').trim().toLowerCase();

  if (!normalizedLocale) {
    return null;
  }

  const matchedLocale = SITE_LANGUAGES.find(
    (entry) => entry.i18nextCode === normalizedLocale || entry.sitePathRoot === normalizedLocale
  );

  return matchedLocale?.i18nextCode || null;
}

export function buildPremiumCheckoutSuccessPath(
  pathname: string,
  params: TXsollaCheckoutSuccessPathParams = {}
): string {
  const localeCode = resolvePremiumCheckoutSuccessLocale(params.locale) || getPathLocale(pathname) || 'en';
  const path = setPathLocale(resolveRoutePath(namedRoutes.PremiumCheckoutSuccess), localeCode);
  const query = new URLSearchParams();

  if (params.tier) {
    query.set('tier', params.tier);
  }

  if (params.billingPeriod) {
    query.set('billing', params.billingPeriod);
  }

  if (params.operation) {
    query.set('operation', params.operation);
  }

  if (params.activated) {
    query.set('activated', '1');
  }

  const queryString = query.toString();
  return `${path}${queryString ? `?${queryString}` : ''}`;
}

export function getPremiumCheckoutSuccessLocaleTestRedirectUrl(pathname: string, search: string): string | null {
  const query = new URLSearchParams(search);
  const requestedLocale = resolvePremiumCheckoutSuccessLocale(query.get('testLocale'));

  if (!requestedLocale) {
    return null;
  }

  query.delete('testLocale');

  const nextPathname = setPathLocale(pathname, requestedLocale);
  const nextQueryString = query.toString();
  const nextUrl = `${nextPathname}${nextQueryString ? `?${nextQueryString}` : ''}`;
  const currentUrl = `${pathname}${search || ''}`;

  return nextUrl === currentUrl ? null : nextUrl;
}

export function parsePremiumCheckoutSuccessSearch(search: string): TXsollaCheckoutSuccessParams {
  const query = new URLSearchParams(search);
  const tier = query.get('tier');
  const billing = query.get('billing');
  const operation = query.get('operation');

  const successParams: TXsollaCheckoutSuccessParams = {
    tier: tier === 'revered' || tier === 'exalted' || tier === 'illustrious' ? tier : null,
    billingPeriod: billing === 'monthly' || billing === 'annual' ? billing : null,
    activated: query.get('activated') === '1'
  };

  if (operation === 'change_plan') {
    successParams.operation = operation;
  }

  return successParams;
}
